import { useEffect, useState, type ReactNode } from "react";

const UNITS = [
  { value: "defer", label: "Henüz doğrulanamadı" },
  { value: "C62", label: "Adet" },
  { value: "KGM", label: "Kilogram (kg)" },
  { value: "GRM", label: "Gram (g)" },
  { value: "LTR", label: "Litre" },
  { value: "MTR", label: "Metre" }
];

type Props = {
  question: {
    title: string;
    units: { key: string; name: string; rows: number }[];
    answer?: string;
    applied?: boolean;
  };
  sourceEvidence?: ReactNode;
  saveOnCalculate?: boolean;
  onDraftChange?: (value: string) => void;
  disabled: boolean;
  onAnswer: (value: string) => void;
};

function savedUnits(question: Props["question"]): Record<string, string> {
  let saved: Record<string, string> = {};
  if (question.answer?.startsWith("custom:")) {
    try { saved = JSON.parse(question.answer.slice(7)) || {}; } catch { /* Missing answers stay empty. */ }
  }
  return Object.fromEntries(question.units.map(row => {
    const value = saved[row.key] || question.answer;
    return [row.key, UNITS.some(unit => unit.value === value) ? value! : "defer"];
  }));
}

export function RecipeUnitQuestion({ question, disabled, onAnswer, sourceEvidence, saveOnCalculate = false, onDraftChange }: Props) {
  const [draft, setDraft] = useState(() => savedUnits(question));
  const [editing, setEditing] = useState(!question.answer);
  const [showMissing, setShowMissing] = useState(false);
  useEffect(() => {
    setDraft(savedUnits(question));
    setEditing(!question.answer);
    setShowMissing(false);
  }, [question.answer]);
  const locked = disabled || Boolean(question.applied);
  const missing = question.units.filter(row => !draft[row.key]);
  const unverified = question.units.filter(row => draft[row.key] === "defer");
  const summary = UNITS.map(unit => {
    const count = question.units.filter(row => draft[row.key] === unit.value).length;
    return count ? `${count} hammadde: ${unit.label}` : "";
  }).filter(Boolean).join(" · ");

  const draftAnswer = `custom:${JSON.stringify(draft)}`;
  useEffect(() => { if (saveOnCalculate) onDraftChange?.(draftAnswer); }, [draftAnswer, saveOnCalculate]);

  function save() {
    setShowMissing(true);
    if (missing.length) return;
    const answer = `custom:${JSON.stringify(Object.fromEntries(question.units.map(row => [row.key, draft[row.key]])))}`;
    if (answer === question.answer) { setEditing(false); return; }
    onAnswer(answer);
  }

  function unitTable(readOnly: boolean) {
    return <div className="smart-unit-table-scroll" role="region" aria-label="Hammadde tüketim birimleri" tabIndex={0}>
      <table className="smart-unit-table">
        <thead><tr><th scope="col">Hammadde</th><th scope="col">Reçetedeki tüketim birimi</th></tr></thead>
        <tbody>{question.units.map((row, index) => <tr key={row.key} className={showMissing && !draft[row.key] ? "incomplete" : ""}>
          <th scope="row"><span>{index + 1}.</span> {row.name}</th>
          <td>{readOnly ? UNITS.find(unit => unit.value === draft[row.key])?.label || "Henüz belirtilmedi" : <select
            aria-label={`${index + 1}. ${row.name} tüketim birimi`}
            aria-invalid={showMissing && !draft[row.key]}
            value={draft[row.key] || ""}
            disabled={locked}
            onChange={event => setDraft(current => ({ ...current, [row.key]: event.target.value }))}
          >
            {UNITS.map(unit => <option key={unit.value} value={unit.value}>{unit.label}</option>)}
          </select>}</td>
        </tr>)}</tbody>
      </table>
    </div>;
  }

  return <article className={`smart-guided-question smart-unit-question${question.answer ? " answered" : ""}`}>
    <div className="smart-unit-heading"><strong>{question.title}</strong><span className="pill">{question.units.length - missing.length - unverified.length} / {question.units.length} birim belirtildi</span></div>
    <p>Her hammaddenin reçetedeki miktarı hangi birimde? Örneğin, 4 parça için <strong>Adet</strong>, 0.5 kg için <strong>Kilogram</strong> seçin. Miktar yazmanız gerekmiyor.</p>
    {sourceEvidence}
    {editing ? <>
      <p>Kaynakta birimi eksik olanlarda “Henüz doğrulanamadı” seçilidir. Bildiklerinizi seçin; HESAPLA bütün seçimleri birlikte kaydeder; belirsiz satırlar doğrulanmış sayılmaz.</p>
      <div className="smart-unit-bulk">
        <strong>Hepsine aynı birimi uygula</strong>
        <p>Çoğu aynıysa önce ortak birimi seçin; farklı olanları aşağıdaki tablodan değiştirin.</p>
        <div>{UNITS.map(unit => <button type="button" className="secondary-button compact" disabled={locked} key={unit.value} onClick={() => {
          setDraft(Object.fromEntries(question.units.map(row => [row.key, unit.value])));
          setShowMissing(false);
        }}>{unit.label}</button>)}</div>
      </div>
      {unitTable(false)}
      {showMissing && missing.length > 0 && <p className="issue error" role="alert">{missing.length} hammaddenin birimi seçilmedi: {missing.slice(0, 3).map(row => row.name).join(", ")}{missing.length > 3 ? "…" : "."} İşaretli satırlardan birim seçin.</p>}
      {saveOnCalculate ? <p>Seçimler HESAPLA’ya bastığınızda birlikte kaydedilip uygulanır.</p> : <div className="smart-unit-actions">
        <button type="button" className="primary-button" disabled={locked} onClick={save}>Seçimleri onayla ve tümünü kaydet</button>
        <button type="button" className="text-button" disabled={locked} onClick={() => onAnswer("defer")}>Henüz doğrulayamıyorum</button>
      </div>}
      <small>Seçimleriniz bu listedeki aynı hammaddeyi kullanan reçete satırlarına uygulanır.</small>
    </> : <>
      <p>{question.answer === "defer" ? "Bu hammaddelerin tüketim birimleri henüz doğrulanmadı." : summary}</p>
      <details className="smart-unit-saved"><summary>Hammadde bazında birimleri göster</summary>{unitTable(true)}</details>
      {!question.applied && <button type="button" className="secondary-button" disabled={disabled} onClick={() => setEditing(true)}>Birimleri düzenle</button>}
    </>}
    {question.answer && <small>{question.applied ? "Cevabınız uygulandı." : "Kaydedildi. HESAPLA’ya basana kadar değiştirebilirsiniz."}</small>}
  </article>;
}
